/**
 * ITU-T E.164 country calling codes.
 *
 * Kilde: ITU-T E.164 (List of Country Calling Codes) + Wikipedia
 * "List of country calling codes".
 *
 * Hvert objekt har:
 *  - iso2:  ISO 3166-1 alpha-2 landkode (f.eks. "NO")
 *  - dialCode: E.164 calling code inkludert ledende "+" (f.eks. "+47")
 *  - nameNo: Norsk navn (for søk/sortering/visning)
 *  - nameEn: Engelsk navn (for søk/sortering/visning)
 *  - flag: Emoji-flagg generert fra iso2
 *
 * Merk: Enkelte dial codes dekker flere land (f.eks. +1 for USA/Canada/Karibia,
 * +44 for UK + oversjøiske territorier). For disse er det kun hovedlandet
 * listet med sin egen iso2.
 *
 * Hvis backend leverer fbo.counties[i].dialCode bør det feltet foretrekkes
 * fremfor denne statiske listen (se CountryDialCodeUtil.toOptionsList), siden
 * backend sitt mkName da vil være riktig lokalisert for gjeldende språk.
 */

export interface CountryDialCode {
  iso2: string;
  dialCode: string;
  /** Navn i gjeldende språk (satt av CountryDialCodeUtil ut fra lang) */
  name: string;
  nameNo: string;
  nameEn: string;
  flag: string;
}

/**
 * Konverterer en ISO 3166-1 alpha-2 landkode til en flagg-emoji
 * ved hjelp av Unicode regional indicator symbols.
 *
 * Eksempel: "NO" -> "🇳🇴", "US" -> "🇺🇸"
 *
 * Merk: På Windows vises flagg-emojis vanligvis som bokstavparet
 * (f.eks. "NO") siden Windows ikke støtter flagg-emojis i fontene.
 * På macOS, iOS, Android og Linux vises flagget korrekt.
 */
export function iso2ToFlagEmoji(iso2: string): string {
  if (!iso2 || iso2.length !== 2) return "";
  return iso2.toUpperCase().replace(/./g, (char) =>
    String.fromCodePoint(0x1f1e6 - 65 + char.charCodeAt(0))
  );
}

// Komplett liste over E.164 calling codes.
const RAW_DIAL_CODES: Array<[string, string, string, string]> = [
  // [iso2, dialCode, nameNo, nameEn]
  ["AF", "+93", "Afghanistan", "Afghanistan"],
  ["AL", "+355", "Albania", "Albania"],
  ["DZ", "+213", "Algerie", "Algeria"],
  ["AS", "+1", "Amerikansk Samoa", "American Samoa"],
  ["AD", "+376", "Andorra", "Andorra"],
  ["AO", "+244", "Angola", "Angola"],
  ["AI", "+1", "Anguilla", "Anguilla"],
  ["AG", "+1", "Antigua og Barbuda", "Antigua and Barbuda"],
  ["AR", "+54", "Argentina", "Argentina"],
  ["AM", "+374", "Armenia", "Armenia"],
  ["AW", "+297", "Aruba", "Aruba"],
  ["AU", "+61", "Australia", "Australia"],
  ["AT", "+43", "Østerrike", "Austria"],
  ["AZ", "+994", "Aserbajdsjan", "Azerbaijan"],
  ["BS", "+1", "Bahamas", "Bahamas"],
  ["BH", "+973", "Bahrain", "Bahrain"],
  ["BD", "+880", "Bangladesh", "Bangladesh"],
  ["BB", "+1", "Barbados", "Barbados"],
  ["BY", "+375", "Hviterussland", "Belarus"],
  ["BE", "+32", "Belgia", "Belgium"],
  ["BZ", "+501", "Belize", "Belize"],
  ["BJ", "+229", "Benin", "Benin"],
  ["BM", "+1", "Bermuda", "Bermuda"],
  ["BT", "+975", "Bhutan", "Bhutan"],
  ["BO", "+591", "Bolivia", "Bolivia"],
  ["BA", "+387", "Bosnia-Hercegovina", "Bosnia and Herzegovina"],
  ["BW", "+267", "Botswana", "Botswana"],
  ["BR", "+55", "Brasil", "Brazil"],
  ["IO", "+246", "Det britiske territoriet i Indiahavet", "British Indian Ocean Territory"],
  ["VG", "+1", "Britiske Jomfruøyer", "British Virgin Islands"],
  ["BN", "+673", "Brunei", "Brunei"],
  ["BG", "+359", "Bulgaria", "Bulgaria"],
  ["BF", "+226", "Burkina Faso", "Burkina Faso"],
  ["BI", "+257", "Burundi", "Burundi"],
  ["KH", "+855", "Kambodsja", "Cambodia"],
  ["CM", "+237", "Kamerun", "Cameroon"],
  ["CA", "+1", "Canada", "Canada"],
  ["CV", "+238", "Kapp Verde", "Cape Verde"],
  ["KY", "+1", "Caymanøyene", "Cayman Islands"],
  ["CF", "+236", "Den sentralafrikanske republikk", "Central African Republic"],
  ["TD", "+235", "Tsjad", "Chad"],
  ["CL", "+56", "Chile", "Chile"],
  ["CN", "+86", "Kina", "China"],
  ["CO", "+57", "Colombia", "Colombia"],
  ["KM", "+269", "Komorene", "Comoros"],
  ["CG", "+242", "Kongo", "Congo"],
  ["CD", "+243", "Kongo (DR)", "Congo (DRC)"],
  ["CK", "+682", "Cookøyene", "Cook Islands"],
  ["CR", "+506", "Costa Rica", "Costa Rica"],
  ["CI", "+225", "Elfenbenskysten", "Côte d'Ivoire"],
  ["HR", "+385", "Kroatia", "Croatia"],
  ["CU", "+53", "Cuba", "Cuba"],
  ["CY", "+357", "Kypros", "Cyprus"],
  ["CZ", "+420", "Tsjekkia", "Czech Republic"],
  ["DK", "+45", "Danmark", "Denmark"],
  ["DJ", "+253", "Djibouti", "Djibouti"],
  ["DM", "+1", "Dominica", "Dominica"],
  ["DO", "+1", "Den dominikanske republikk", "Dominican Republic"],
  ["EC", "+593", "Ecuador", "Ecuador"],
  ["EG", "+20", "Egypt", "Egypt"],
  ["SV", "+503", "El Salvador", "El Salvador"],
  ["GQ", "+240", "Ekvatorial-Guinea", "Equatorial Guinea"],
  ["ER", "+291", "Eritrea", "Eritrea"],
  ["EE", "+372", "Estland", "Estonia"],
  ["ET", "+251", "Etiopia", "Ethiopia"],
  ["FK", "+500", "Falklandsøyene", "Falkland Islands"],
  ["FO", "+298", "Færøyene", "Faroe Islands"],
  ["FJ", "+679", "Fiji", "Fiji"],
  ["FI", "+358", "Finland", "Finland"],
  ["FR", "+33", "Frankrike", "France"],
  ["GF", "+594", "Fransk Guyana", "French Guiana"],
  ["PF", "+689", "Fransk Polynesia", "French Polynesia"],
  ["GA", "+241", "Gabon", "Gabon"],
  ["GM", "+220", "Gambia", "Gambia"],
  ["GE", "+995", "Georgia", "Georgia"],
  ["DE", "+49", "Tyskland", "Germany"],
  ["GH", "+233", "Ghana", "Ghana"],
  ["GI", "+350", "Gibraltar", "Gibraltar"],
  ["GR", "+30", "Hellas", "Greece"],
  ["GL", "+299", "Grønland", "Greenland"],
  ["GD", "+1", "Grenada", "Grenada"],
  ["GP", "+590", "Guadeloupe", "Guadeloupe"],
  ["GU", "+1", "Guam", "Guam"],
  ["GT", "+502", "Guatemala", "Guatemala"],
  ["GG", "+44", "Guernsey", "Guernsey"],
  ["GN", "+224", "Guinea", "Guinea"],
  ["GW", "+245", "Guinea-Bissau", "Guinea-Bissau"],
  ["GY", "+592", "Guyana", "Guyana"],
  ["HT", "+509", "Haiti", "Haiti"],
  ["HN", "+504", "Honduras", "Honduras"],
  ["HK", "+852", "Hongkong", "Hong Kong"],
  ["HU", "+36", "Ungarn", "Hungary"],
  ["IS", "+354", "Island", "Iceland"],
  ["IN", "+91", "India", "India"],
  ["ID", "+62", "Indonesia", "Indonesia"],
  ["IR", "+98", "Iran", "Iran"],
  ["IQ", "+964", "Irak", "Iraq"],
  ["IE", "+353", "Irland", "Ireland"],
  ["IM", "+44", "Isle of Man", "Isle of Man"],
  ["IL", "+972", "Israel", "Israel"],
  ["IT", "+39", "Italia", "Italy"],
  ["JM", "+1", "Jamaica", "Jamaica"],
  ["JP", "+81", "Japan", "Japan"],
  ["JE", "+44", "Jersey", "Jersey"],
  ["JO", "+962", "Jordan", "Jordan"],
  ["KZ", "+7", "Kasakhstan", "Kazakhstan"],
  ["KE", "+254", "Kenya", "Kenya"],
  ["KI", "+686", "Kiribati", "Kiribati"],
  ["KP", "+850", "Nord-Korea", "Korea (North)"],
  ["KR", "+82", "Sør-Korea", "Korea (South)"],
  ["KW", "+965", "Kuwait", "Kuwait"],
  ["KG", "+996", "Kirgisistan", "Kyrgyzstan"],
  ["LA", "+856", "Laos", "Laos"],
  ["LV", "+371", "Latvia", "Latvia"],
  ["LB", "+961", "Libanon", "Lebanon"],
  ["LS", "+266", "Lesotho", "Lesotho"],
  ["LR", "+231", "Liberia", "Liberia"],
  ["LY", "+218", "Libya", "Libya"],
  ["LI", "+423", "Liechtenstein", "Liechtenstein"],
  ["LT", "+370", "Litauen", "Lithuania"],
  ["LU", "+352", "Luxembourg", "Luxembourg"],
  ["MO", "+853", "Macao", "Macau"],
  ["MK", "+389", "Nord-Makedonia", "Macedonia"],
  ["MG", "+261", "Madagaskar", "Madagascar"],
  ["MW", "+265", "Malawi", "Malawi"],
  ["MY", "+60", "Malaysia", "Malaysia"],
  ["MV", "+960", "Maldivene", "Maldives"],
  ["ML", "+223", "Mali", "Mali"],
  ["MT", "+356", "Malta", "Malta"],
  ["MH", "+692", "Marshalløyene", "Marshall Islands"],
  ["MQ", "+596", "Martinique", "Martinique"],
  ["MR", "+222", "Mauritania", "Mauritania"],
  ["MU", "+230", "Mauritius", "Mauritius"],
  ["YT", "+262", "Mayotte", "Mayotte"],
  ["MX", "+52", "Mexico", "Mexico"],
  ["FM", "+691", "Mikronesiaføderasjonen", "Micronesia"],
  ["MD", "+373", "Moldova", "Moldova"],
  ["MC", "+377", "Monaco", "Monaco"],
  ["MN", "+976", "Mongolia", "Mongolia"],
  ["ME", "+382", "Montenegro", "Montenegro"],
  ["MS", "+1", "Montserrat", "Montserrat"],
  ["MA", "+212", "Marokko", "Morocco"],
  ["MZ", "+258", "Mosambik", "Mozambique"],
  ["MM", "+95", "Myanmar", "Myanmar"],
  ["NA", "+264", "Namibia", "Namibia"],
  ["NR", "+674", "Nauru", "Nauru"],
  ["NP", "+977", "Nepal", "Nepal"],
  ["NL", "+31", "Nederland", "Netherlands"],
  ["NC", "+687", "Ny-Caledonia", "New Caledonia"],
  ["NZ", "+64", "New Zealand", "New Zealand"],
  ["NI", "+505", "Nicaragua", "Nicaragua"],
  ["NE", "+227", "Niger", "Niger"],
  ["NG", "+234", "Nigeria", "Nigeria"],
  ["NU", "+683", "Niue", "Niue"],
  ["NF", "+672", "Norfolkøya", "Norfolk Island"],
  ["NO", "+47", "Norge", "Norway"],
  ["OM", "+968", "Oman", "Oman"],
  ["PK", "+92", "Pakistan", "Pakistan"],
  ["PW", "+680", "Palau", "Palau"],
  ["PS", "+970", "Palestina", "Palestine"],
  ["PA", "+507", "Panama", "Panama"],
  ["PG", "+675", "Papua Ny-Guinea", "Papua New Guinea"],
  ["PY", "+595", "Paraguay", "Paraguay"],
  ["PE", "+51", "Peru", "Peru"],
  ["PH", "+63", "Filippinene", "Philippines"],
  ["PL", "+48", "Polen", "Poland"],
  ["PT", "+351", "Portugal", "Portugal"],
  ["PR", "+1", "Puerto Rico", "Puerto Rico"],
  ["QA", "+974", "Qatar", "Qatar"],
  ["RE", "+262", "Réunion", "Réunion"],
  ["RO", "+40", "Romania", "Romania"],
  ["RU", "+7", "Russland", "Russia"],
  ["RW", "+250", "Rwanda", "Rwanda"],
  ["BL", "+590", "Saint-Barthélemy", "Saint Barthélemy"],
  ["SH", "+290", "Saint Helena", "Saint Helena"],
  ["KN", "+1", "Saint Kitts og Nevis", "Saint Kitts and Nevis"],
  ["LC", "+1", "Saint Lucia", "Saint Lucia"],
  ["MF", "+590", "Saint-Martin", "Saint Martin"],
  ["PM", "+508", "Saint-Pierre-et-Miquelon", "Saint Pierre and Miquelon"],
  ["VC", "+1", "Saint Vincent og Grenadinene", "Saint Vincent and the Grenadines"],
  ["WS", "+685", "Samoa", "Samoa"],
  ["SM", "+378", "San Marino", "San Marino"],
  ["ST", "+239", "São Tomé og Príncipe", "São Tomé and Príncipe"],
  ["SA", "+966", "Saudi-Arabia", "Saudi Arabia"],
  ["SN", "+221", "Senegal", "Senegal"],
  ["RS", "+381", "Serbia", "Serbia"],
  ["SC", "+248", "Seychellene", "Seychelles"],
  ["SL", "+232", "Sierra Leone", "Sierra Leone"],
  ["SG", "+65", "Singapore", "Singapore"],
  ["SX", "+1", "Sint Maarten", "Sint Maarten"],
  ["SK", "+421", "Slovakia", "Slovakia"],
  ["SI", "+386", "Slovenia", "Slovenia"],
  ["SB", "+677", "Salomonøyene", "Solomon Islands"],
  ["SO", "+252", "Somalia", "Somalia"],
  ["ZA", "+27", "Sør-Afrika", "South Africa"],
  ["SS", "+211", "Sør-Sudan", "South Sudan"],
  ["ES", "+34", "Spania", "Spain"],
  ["LK", "+94", "Sri Lanka", "Sri Lanka"],
  ["SD", "+249", "Sudan", "Sudan"],
  ["SR", "+597", "Surinam", "Suriname"],
  ["SZ", "+268", "Swaziland (Eswatini)", "Swaziland"],
  ["SE", "+46", "Sverige", "Sweden"],
  ["CH", "+41", "Sveits", "Switzerland"],
  ["SY", "+963", "Syria", "Syria"],
  ["TW", "+886", "Taiwan", "Taiwan"],
  ["TJ", "+992", "Tadsjikistan", "Tajikistan"],
  ["TZ", "+255", "Tanzania", "Tanzania"],
  ["TH", "+66", "Thailand", "Thailand"],
  ["TL", "+670", "Øst-Timor", "Timor-Leste"],
  ["TG", "+228", "Togo", "Togo"],
  ["TK", "+690", "Tokelau", "Tokelau"],
  ["TO", "+676", "Tonga", "Tonga"],
  ["TT", "+1", "Trinidad og Tobago", "Trinidad and Tobago"],
  ["TN", "+216", "Tunisia", "Tunisia"],
  ["TR", "+90", "Tyrkia", "Turkey"],
  ["TM", "+993", "Turkmenistan", "Turkmenistan"],
  ["TC", "+1", "Turks- og Caicosøyene", "Turks and Caicos Islands"],
  ["TV", "+688", "Tuvalu", "Tuvalu"],
  ["UG", "+256", "Uganda", "Uganda"],
  ["UA", "+380", "Ukraina", "Ukraine"],
  ["AE", "+971", "De forente arabiske emirater", "United Arab Emirates"],
  ["GB", "+44", "Storbritannia", "United Kingdom"],
  ["US", "+1", "USA", "United States"],
  ["UY", "+598", "Uruguay", "Uruguay"],
  ["UZ", "+998", "Usbekistan", "Uzbekistan"],
  ["VU", "+678", "Vanuatu", "Vanuatu"],
  ["VA", "+379", "Vatikanstaten", "Vatican City"],
  ["VE", "+58", "Venezuela", "Venezuela"],
  ["VN", "+84", "Vietnam", "Vietnam"],
  ["VI", "+1", "Amerikanske Jomfruøyer", "U.S. Virgin Islands"],
  ["WF", "+681", "Wallis og Futuna", "Wallis and Futuna"],
  ["YE", "+967", "Jemen", "Yemen"],
  ["ZM", "+260", "Zambia", "Zambia"],
  ["ZW", "+263", "Zimbabwe", "Zimbabwe"],
];

export function buildCountryDialCode(
  iso2: string,
  dialCode: string,
  nameNo: string,
  nameEn: string,
  lang: string = "no"
): CountryDialCode {
  return {
    iso2,
    dialCode,
    nameNo,
    nameEn,
    name: lang === "en" ? (nameEn || nameNo) : (nameNo || nameEn),
    flag: iso2ToFlagEmoji(iso2),
  };
}

/**
 * Returnerer hele den statiske dial code-listen for et gitt språk ("no" | "en").
 * `name`-feltet på hvert element settes ut fra `lang`.
 */
export function getCountryDialCodes(lang: string = "no"): CountryDialCode[] {
  return RAW_DIAL_CODES.map(([iso2, dialCode, nameNo, nameEn]) =>
    buildCountryDialCode(iso2, dialCode, nameNo, nameEn, lang)
  );
}

// Statisk liste med norske navn som default (bakoverkompatibel eksport).
export const COUNTRY_DIAL_CODES: CountryDialCode[] = getCountryDialCodes("no");

/**
 * Utility-funksjoner for dial code / telefonnummer-håndtering.
 */
export class CountryDialCodeUtil {
  /**
   * Returnerer dial code-liste formatert for bruk i DynformControl
   * med controlType "telephone".
   *
   * Hvis counties-listen fra backend har dialCode-felt, brukes det (med
   * backend sitt allerede lokaliserte mkName/name). Ellers fallback til
   * den statiske listen, lokalisert til `lang` ("no" | "en", default "no").
   */
  static toOptionsList(
    counties?: any[],
    preferredIso2: string[] = ["NO", "SE", "DK", "GB", "DE", "US"],
    lang: string = "no"
  ): CountryDialCode[] {
    let list: CountryDialCode[];

    if (counties && counties.length && counties[0]?.dialCode) {
      // Backend leverer dialCode på counties - bruk backend sitt navn (allerede lokalisert)
      list = counties
        .filter((c: any) => c.dialCode)
        .map((c: any) => {
          let name = c.name || c.mkName || c.code || "";
          return {
            iso2: c.code || c.iso2 || "",
            dialCode: c.dialCode,
            name,
            nameNo: name,
            nameEn: name,
            flag: iso2ToFlagEmoji(c.code || c.iso2 || ""),
          };
        });
    } else {
      list = getCountryDialCodes(lang);
    }

    // Sorter: preferred først (i angitt rekkefølge), deretter resten alfabetisk
    const preferred = preferredIso2
      .map((iso2) => list.find((c) => c.iso2 === iso2))
      .filter((c): c is CountryDialCode => !!c);

    const rest = list
      .filter((c) => !preferredIso2.includes(c.iso2))
      .sort((a, b) => a.name.localeCompare(b.name));

    return [...preferred, ...rest];
  }

  /**
   * Finner dial code for et gitt ISO2-landkode.
   */
  static findByIso2(iso2: string): CountryDialCode | undefined {
    return COUNTRY_DIAL_CODES.find((c) => c.iso2 === iso2.toUpperCase());
  }

  /**
   * Finner dial code-objekt ut fra dial code-streng (f.eks. "+47").
   */
  static findByDialCode(dialCode: string): CountryDialCode | undefined {
    const normalized = dialCode.startsWith("+")
      ? dialCode
      : "+" + dialCode.replace(/^00/, "");
    return COUNTRY_DIAL_CODES.find((c) => c.dialCode === normalized);
  }

  /**
   * Finner alle ISO2-landkoder som deler en gitt dial code.
   * F.eks. "+1" -> ["US", "CA", "BS", ...]
   */
  static findIso2ByDialCode(dialCode: string): string[] {
    const normalized = dialCode.startsWith("+")
      ? dialCode
      : "+" + dialCode.replace(/^00/, "");
    return COUNTRY_DIAL_CODES.filter(
      (c) => c.dialCode === normalized
    ).map((c) => c.iso2);
  }

  /**
   * Splitter et telefonnummer i prefix (dial code) og lokalt nummer.
   *
   * Eksempler:
   *  "+4799887766"  -> { dialCode: "+47", local: "99887766" }
   *  "99887766"     -> { dialCode: "+47", local: "99887766" }  (default +47)
   *  "004799887766" -> { dialCode: "+47", local: "99887766" }
   *  "+1 555 1234567" -> { dialCode: "+1",  local: "5551234567" }
   *
   * Returnerer alltid et objekt; dialCode er "+47" hvis ikke funnet.
   */
  static splitTelephone(
    telephone: string | undefined | null,
    defaultDialCode: string = "+47"
  ): { dialCode: string; local: string } {
    if (!telephone) return { dialCode: defaultDialCode, local: "" };

    // Rens: fjern mellomrom, bindestreker, parenteser
    let cleaned = telephone.replace(/[\s\-\(\)]/g, "");

    // Erstatt ledende 00 med +
    if (cleaned.startsWith("00")) {
      cleaned = "+" + cleaned.substring(2);
    }

    // Hvis ikke starter med +, anta default prefix
    if (!cleaned.startsWith("+")) {
      return { dialCode: defaultDialCode, local: cleaned };
    }

    // Prøv å matche lengste dial code først (f.eks. +1 vs +12...)
    // Dial codes er 1-3 siffer etter +
    for (let len = 3; len >= 1; len--) {
      const candidate = cleaned.substring(0, len + 1); // inkluderer "+"
      const match = COUNTRY_DIAL_CODES.find((c) => c.dialCode === candidate);
      if (match) {
        return {
          dialCode: match.dialCode,
          local: cleaned.substring(len + 1),
        };
      }
    }

    // Kunne ikke matche; returner hele som local med default prefix
    return { dialCode: defaultDialCode, local: cleaned.replace(/^\+/, "") };
  }

  /**
   * Kombinerer dial code og lokalt nummer til én streng.
   * Fjerner mellomrom fra local.
   *
   * NB: Returnerer den valgte prefiksen selv om `local` er tom (f.eks. "+46").
   * Dette er bevisst, slik at et brukervalgt prefiks ALDRI går tapt bare
   * fordi feltet for lokalnummer ennå er tomt (f.eks. hvis bruker velger
   * land før de rekker å skrive inn nummeret). Bruk
   * `CountryDialCodeUtil.isTelephoneEmpty()` for å sjekke om verdien
   * reelt sett mangler et telefonnummer (for required-validering).
   */
  static combineTelephone(dialCode: string, local: string): string {
    const cleanLocal = (local || "").replace(/[\s\-\(\)]/g, "");
    const prefix = dialCode.startsWith("+") ? dialCode : "+" + dialCode;
    return prefix + cleanLocal;
  }

  /**
   * Sjekker om en telephone-verdi reelt sett mangler et lokalnummer, dvs.
   * om den kun består av (eller er) en prefiks uten faktiske sifre etter.
   * Brukes av required-valideringen for controlType "telephone", siden
   * selve FormControl-verdien alltid kan inneholde en prefiks (se
   * combineTelephone) selv når brukeren ikke har skrevet inn et nummer.
   */
  static isTelephoneEmpty(value: string | undefined | null): boolean {
    if (!value) return true;
    return CountryDialCodeUtil.splitTelephone(value).local === "";
  }
}
